import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  TextField,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Chip,
  Divider
} from '@mui/material';
import {
  Search,
  Chat,
  People,
  Label,
  Assignment,
  Settings,
  Dashboard,
  Assessment,
  Schedule,
  Group,
  QuestionAnswer,
  Add,
  KeyboardArrowRight
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';

interface Command {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  action: () => void;
  category: 'navigation' | 'conversation' | 'contact' | 'system';
  keywords: string[];
  shortcut?: string;
}

interface CommandBarProps {
  open: boolean;
  onClose: () => void;
}

const CommandBar: React.FC<CommandBarProps> = ({ open, onClose }) => {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const navigate = useNavigate();
  const { conversations } = useAppSelector(state => state.conversations);

  const commands: Command[] = useMemo(() => [
    // Navigation Commands
    {
      id: 'nav-dashboard',
      title: 'Go to Dashboard',
      description: 'View dashboard overview',
      icon: <Dashboard />,
      action: () => navigate('/dashboard'),
      category: 'navigation',
      keywords: ['dashboard', 'home', 'overview'],
      shortcut: 'Ctrl+D'
    },
    {
      id: 'nav-conversations',
      title: 'Go to Conversations',
      description: 'View all conversations',
      icon: <Chat />,
      action: () => navigate('/dashboard/conversations'),
      category: 'navigation',
      keywords: ['conversations', 'chat', 'messages'],
      shortcut: 'Ctrl+C'
    },
    {
      id: 'nav-contacts',
      title: 'Go to Linelist',
      description: 'View all contacts',
      icon: <People />,
      action: () => navigate('/dashboard/contacts'),
      category: 'navigation',
      keywords: ['contacts', 'customers', 'people'],
      shortcut: 'Ctrl+P'
    },
    {
      id: 'nav-reports',
      title: 'Go to Reports',
      description: 'View analytics and reports',
      icon: <Assessment />,
      action: () => navigate('/dashboard/reports'),
      category: 'navigation',
      keywords: ['reports', 'analytics', 'stats'],
      shortcut: 'Ctrl+R'
    },
    {
      id: 'nav-teams',
      title: 'Go to Teams',
      description: 'Manage teams and members',
      icon: <Group />,
      action: () => navigate('/dashboard/teams'),
      category: 'navigation',
      keywords: ['teams', 'members', 'agents']
    },
    {
      id: 'nav-labels',
      title: 'Go to Labels',
      description: 'Manage conversation labels',
      icon: <Label />,
      action: () => navigate('/dashboard/labels'),
      category: 'navigation',
      keywords: ['labels', 'tags', 'categories']
    },
    {
      id: 'nav-canned',
      title: 'Go to Canned Responses',
      description: 'Manage canned responses',
      icon: <QuestionAnswer />,
      action: () => navigate('/dashboard/canned-responses'),
      category: 'navigation',
      keywords: ['canned', 'responses', 'templates']
    },
    {
      id: 'nav-settings',
      title: 'Go to Settings',
      description: 'Application settings',
      icon: <Settings />,
      action: () => navigate('/dashboard/settings'),
      category: 'navigation',
      keywords: ['settings', 'config', 'preferences']
    },

    // Conversation Commands
    {
      id: 'conv-new',
      title: 'New Conversation',
      description: 'Start a new conversation',
      icon: <Add />,
      action: () => {
        // Implementation for creating new conversation
        console.log('Creating new conversation');
      },
      category: 'conversation',
      keywords: ['new', 'create', 'conversation', 'start'],
      shortcut: 'Ctrl+N'
    },
    {
      id: 'conv-assign',
      title: 'Assign Conversation',
      description: 'Assign current conversation to agent',
      icon: <Assignment />,
      action: () => {
        // Implementation for assigning conversation
        console.log('Assigning conversation');
      },
      category: 'conversation',
      keywords: ['assign', 'agent', 'transfer']
    },
    {
      id: 'conv-resolve',
      title: 'Resolve Conversation',
      description: 'Mark current conversation as resolved',
      icon: <Schedule />,
      action: () => {
        // Implementation for resolving conversation
        console.log('Resolving conversation');
      },
      category: 'conversation',
      keywords: ['resolve', 'close', 'complete'],
      shortcut: 'Ctrl+Shift+R'
    },

    // Contact Commands
    {
      id: 'contact-new',
      title: 'New Contact',
      description: 'Create a new contact',
      icon: <Add />,
      action: () => navigate('/dashboard/contacts/new'),
      category: 'contact',
      keywords: ['new', 'create', 'contact', 'customer']
    },

    // System Commands
    {
      id: 'system-logout',
      title: 'Logout',
      description: 'Sign out of the application',
      icon: <Settings />,
      action: () => {
        // Implementation for logout
        console.log('Logging out');
      },
      category: 'system',
      keywords: ['logout', 'signout', 'exit']
    }
  ], [navigate]);

  // Add recent conversations as commands
  const recentConversationsCommands: Command[] = useMemo(() => 
    conversations.slice(0, 5).map(conv => ({
      id: `conv-${conv.id}`,
      title: `Open conversation with ${conv.contact?.name || 'Unknown'}`,
      description: `#${conv.display_id} - ${conv.status}`,
      icon: <Chat />,
      action: () => navigate(`/dashboard/conversations/${conv.id}`),
      category: 'conversation' as const,
      keywords: [conv.contact?.name || '', conv.display_id.toString(), 'conversation']
    })), [conversations, navigate]
  );

  const allCommands = [...commands, ...recentConversationsCommands];

  const filteredCommands = useMemo(() => {
    if (!query.trim()) return allCommands;
    
    const lowercaseQuery = query.toLowerCase();
    return allCommands.filter(command => 
      command.title.toLowerCase().includes(lowercaseQuery) ||
      command.description.toLowerCase().includes(lowercaseQuery) ||
      command.keywords.some(keyword => keyword.toLowerCase().includes(lowercaseQuery))
    );
  }, [query, allCommands]);

  const groupedCommands = useMemo(() => {
    const groups: Record<string, Command[]> = {};
    filteredCommands.forEach(command => {
      if (!groups[command.category]) {
        groups[command.category] = [];
      }
      groups[command.category].push(command);
    });
    return groups;
  }, [filteredCommands]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setSelectedIndex(0);
    }
  }, [open]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!open) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setSelectedIndex(prev => Math.min(prev + 1, filteredCommands.length - 1));
          break;
        case 'ArrowUp':
          event.preventDefault();
          setSelectedIndex(prev => Math.max(prev - 1, 0));
          break;
        case 'Enter':
          event.preventDefault();
          if (filteredCommands[selectedIndex]) {
            filteredCommands[selectedIndex].action();
            onClose();
          }
          break;
        case 'Escape':
          onClose();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, filteredCommands, selectedIndex, onClose]);

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'navigation': return <Dashboard />;
      case 'conversation': return <Chat />;
      case 'contact': return <People />;
      case 'system': return <Settings />;
      default: return <Search />;
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'navigation': return 'Navigation';
      case 'conversation': return 'Conversations';
      case 'contact': return 'Contacts';
      case 'system': return 'System';
      default: return 'Other';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          position: 'fixed',
          top: '20%',
          transform: 'translateY(-20%)',
          borderRadius: 2,
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
        }
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <TextField
            fullWidth
            placeholder="Type a command or search..."
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelectedIndex(0);
            }}
            autoFocus
            InputProps={{
              startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                border: 'none',
                '& fieldset': {
                  border: 'none',
                },
              },
            }}
          />
        </Box>

        <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
          {Object.keys(groupedCommands).length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">
                No commands found
              </Typography>
            </Box>
          ) : (
            Object.entries(groupedCommands).map(([category, commands], categoryIndex) => (
              <Box key={category}>
                <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {getCategoryIcon(category)}
                    <Typography variant="subtitle2" sx={{ ml: 1, fontWeight: 600 }}>
                      {getCategoryLabel(category)}
                    </Typography>
                  </Box>
                </Box>
                <List sx={{ py: 0 }}>
                  {commands.map((command, index) => {
                    const globalIndex = filteredCommands.findIndex(c => c.id === command.id);
                    const isSelected = globalIndex === selectedIndex;
                    
                    return (
                      <ListItem
                        key={command.id}
                        button
                        selected={isSelected}
                        onClick={() => {
                          command.action();
                          onClose();
                        }}
                        sx={{
                          py: 1.5,
                          '&.Mui-selected': {
                            backgroundColor: 'primary.light',
                            color: 'primary.contrastText',
                          },
                        }}
                      >
                        <ListItemIcon sx={{ color: isSelected ? 'inherit' : 'inherit' }}>
                          {command.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={command.title}
                          secondary={command.description}
                          secondaryTypographyProps={{
                            color: isSelected ? 'inherit' : 'text.secondary',
                            sx: { opacity: isSelected ? 0.8 : 1 }
                          }}
                        />
                        {command.shortcut && (
                          <Chip
                            label={command.shortcut}
                            size="small"
                            variant="outlined"
                            sx={{
                              ml: 1,
                              fontSize: '0.75rem',
                              color: isSelected ? 'inherit' : 'text.secondary'
                            }}
                          />
                        )}
                        <KeyboardArrowRight sx={{ color: isSelected ? 'inherit' : 'text.secondary' }} />
                      </ListItem>
                    );
                  })}
                </List>
                {categoryIndex < Object.keys(groupedCommands).length - 1 && <Divider />}
              </Box>
            ))
          )}
        </Box>

        <Box sx={{ p: 2, bgcolor: 'grey.50', borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary">
            Use ↑↓ to navigate, Enter to select, Esc to close
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CommandBar;